// SPDX-License-Identifier: Elastic-2.0
/**
 * wsBridge — wire the WebSocket event bus into TanStack Query (T-023).
 *
 * Subscribes to the typed `wsEventBus` (T-008) and translates incoming
 * server-pushed frames into `queryClient.invalidateQueries(...)` calls
 * against the cache keys the dashboard hooks consume:
 *
 *   - `device_state`        → `["devices", device_id]` + `["devices"]` (exact)
 *   - `suggestion_new`      → `["suggestions"]`
 *   - `suggestion_updated`  → `["suggestions"]`
 *   - `routine_triggered`   → `["routines"]`
 *
 * Why this lives next to `DashboardLayout` (not in `api/`):
 *   - The invalidation map is dashboard-domain knowledge — the WS layer
 *     itself stays event-name-only and doesn't know about TanStack keys.
 *   - Mounting the effect from the layout means the bridge is alive for
 *     every authenticated route (`/dashboard`, `/routines`, `/suggestions`,
 *     `/settings`) but never burns subscriptions on the public surface
 *     (`/login`, onboarding, etc.).
 *
 * Design notes:
 *   - The effect MUST run exactly once per layout mount. We rely on
 *     React 18 strict-mode double-mount being idempotent here: each
 *     `wsEventBus.on(...)` returns its own unsubscribe, and the cleanup
 *     unwires every listener it registered, so a double-invoke leaves
 *     zero stale subscriptions.
 *   - `queryClient` is read from context (`useQueryClient`) — the
 *     `queryClient` import path stays decoupled, so tests can mount a
 *     fresh client per spec without monkey-patching the module.
 *   - `["devices"]` invalidation uses `exact: true` so per-device detail
 *     queries (`["devices", id]`) aren't double-invalidated when both
 *     a list refetch and a targeted detail refetch would fire (we issue
 *     the targeted one separately above).
 *
 * Per F-310 technical spec § 9 (WebSocket strategy).
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { wsEventBus } from "@/api/wsEventBus";

/**
 * Mount the WS → TanStack Query bridge for the lifetime of the calling
 * component. Call once at the top of `DashboardLayout`. Returns nothing
 * (the effect handles all subscription bookkeeping internally).
 */
export function useWebSocketBridge(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const offDeviceState = wsEventBus.on("device_state", ({ device_id }) => {
      // Targeted detail key first so an open device-control modal
      // (T-024) refetches its authoritative state. The list key gets
      // an `exact: true` invalidation so we don't loop through every
      // matching child key — the targeted call above already covered
      // the one we care about.
      queryClient.invalidateQueries({ queryKey: ["devices", device_id] });
      queryClient.invalidateQueries({ queryKey: ["devices"], exact: true });
    });

    const offSuggestionNew = wsEventBus.on("suggestion_new", () => {
      queryClient.invalidateQueries({ queryKey: ["suggestions"] });
    });

    const offSuggestionUpdated = wsEventBus.on("suggestion_updated", () => {
      queryClient.invalidateQueries({ queryKey: ["suggestions"] });
    });

    const offRoutineTriggered = wsEventBus.on("routine_triggered", () => {
      queryClient.invalidateQueries({ queryKey: ["routines"] });
    });

    return () => {
      offDeviceState();
      offSuggestionNew();
      offSuggestionUpdated();
      offRoutineTriggered();
    };
  }, [queryClient]);
}
